import * as React from "react";
import uniqueId from "lodash/fp/uniqueId";
import maxBy from "lodash/fp/maxBy";
import reverse from "lodash/fp/reverse";
import uniq from "lodash/fp/uniq";

export const generateLayoutElementComponent =
  (type: string) =>
  ({
    priority,
    children,
  }: {
    priority?: number;
    children: React.ReactNode;
  }) => {
    const { getFunctions } = React.useContext(LayoutPositionerContext);
    const { render, remove } = getFunctions(type, priority);

    React.useLayoutEffect(() => {
      render(children);
    });

    React.useEffect(() => {
      return () => {
        remove();
      };
    }, [remove]);

    return null;
  };

interface PositionerAPI {
  getFunctions(
    type: string,
    priority?: number
  ): {
    render: (content: React.ReactNode) => void;
    remove: () => void;
  };
}

const noopPositionerAPI = {
  getFunctions: () => ({
    render: () => undefined,
    remove: () => undefined,
  }),
};

const LayoutPositionerContext =
  React.createContext<PositionerAPI>(noopPositionerAPI);

interface LayoutPositionerProps {
  children(fn: { [key: string]: React.ReactNode }): React.ReactNode;
}

export const LayoutPositioner = ({ children }: LayoutPositionerProps) => {
  const [elements, setElements] = React.useState<{
    [key: string]: {
      content: React.ReactNode;
      type: string;
      priority?: number;
    };
  }>({});

  const renderElement = React.useCallback((key, element) => {
    setElements((current) => {
      const newElements = { ...current };
      // NOTE: Make sure to maintain original chronological order of properties
      newElements[key] = element;
      return newElements;
    });
  }, []);

  const removeElement = React.useCallback((key: string) => {
    setElements((current) => {
      const newElements = { ...current };
      delete newElements[key];
      return newElements;
    });
  }, []);

  const getFunctions = React.useCallback(
    (type: string, priority?: number) => {
      const key = uniqueId("element_");
      const render = (content: React.ReactNode) =>
        renderElement(key, { type, priority, content });
      const remove = () => removeElement(key);
      return { render, remove };
    },
    [renderElement, removeElement]
  );

  const value = React.useMemo(
    () => ({
      getFunctions,
    }),
    [getFunctions]
  );

  const finalElementsToRender = React.useMemo(() => {
    // NOTE: "arr" will be populated in chronological order of properties
    const arr = Object.values(elements);
    const reverted = reverse(arr);
    const types = uniq(reverted.map((e) => e.type));
    return types.reduce<{ [key: string]: React.ReactNode }>((acc, type) => {
      acc[type] = maxBy(
        (e) => e.priority || 0,
        reverted.filter((e) => e.type === type)
      )?.content;
      return acc;
    }, {});
  }, [elements]);

  return (
    <LayoutPositionerContext.Provider value={value}>
      {children(finalElementsToRender)}
    </LayoutPositionerContext.Provider>
  );
};
